import type { DeadlineProof, DeadlineNoticeAnchor } from '../../customerDeadlines';
import { API_BASE } from '../http';
export type { DeadlineNoticeAnchor };
import type { RequestFn } from '../http';
import type {
  CustomerDeadlineBase,
  CustomerDeadlineCategory,
  CustomerDeadlineOwnership,
  CustomerDeadlineTermType,
} from '../../customerDeadlines';

/**
 * Fristen-Register (Techniker-Seite): Lizenzen, Server, Domains, Zertifikate, Verträge
 * die fixbyte für den Kunden bereitstellt oder verwaltet. Gepflegt im Kunden-Flyout.
 */

interface TechnicianDeadlineExtra {
  reminderTicketNumber?: string | null;
  /** Stückzahl — nur intern; im Kundenportal steht sie nie */
  quantity: number | null;
  internalNote: string | null;
  visibleInPortal: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Posten mit eigener Laufzeit — mit Ampel und Handlungsfrist. */
export interface TechnicianFixedDeadline extends CustomerDeadlineBase, TechnicianDeadlineExtra {
  termType: 'FIXED';
  customerId: string;
}

/** Ohne Laufzeitende — Managed Service oder Laufzeit angefragt: ohne Frist und Ampel. */
export interface TechnicianServiceItem
  extends Omit<CustomerDeadlineBase, 'endDate' | 'termEndDate' | 'actionDate' | 'daysLeft' | 'status' | 'actionIsCancellation'>,
    TechnicianDeadlineExtra {
  termType: 'SERVICE_CONTRACT' | 'PENDING';
  customerId: string;
  endDate: null;
  termEndDate: null;
  actionDate: null;
  daysLeft: null;
  status: null;
  actionIsCancellation: false;
}

export type TechnicianDeadline = TechnicianFixedDeadline | TechnicianServiceItem;

export interface UpcomingDeadline extends TechnicianFixedDeadline {
  customer: { id: string; name: string; weclappCustomerId: string | null; cwCompanyId: number | null };
}

export interface CustomerDeadlineInput {
  category?: CustomerDeadlineCategory;
  ownership?: CustomerDeadlineOwnership;
  name?: string;
  /** '' entfernt den Wert */
  provider?: string;
  reference?: string;
  quantity?: number | null;
  /** YYYY-MM-DD; null entfernt */
  startDate?: string | null;
  /** PENDING = Laufzeit angefragt; SERVICE_CONTRACT = läuft mit dem FixByte-Servicevertrag */
  termType?: CustomerDeadlineTermType;
  /** null nur mit termType PENDING oder SERVICE_CONTRACT */
  endDate?: string | null;
  autoRenew?: boolean;
  renewalMonths?: number | null;
  noticeDays?: number | null;
  noticeAnchor?: DeadlineNoticeAnchor;
  customerNote?: string;
  internalNote?: string;
  visibleInPortal?: boolean;
  isActive?: boolean;
}

/** Von KIAN aus einem Dokument gelesene Vertrags-/Lizenzfelder. Alles optional. */
export interface DeadlinePrefill {
  category:
    | 'LICENSE' | 'SERVER' | 'HOSTING' | 'DOMAIN'
    | 'CERTIFICATE' | 'CONTRACT' | 'SUPPORT' | 'OTHER'
    | null;
  name: string | null;
  provider: string | null;
  reference: string | null;
  quantity: number | null;
  startDate: string | null;
  endDate: string | null;
  autoRenew: boolean | null;
  renewalMonths: number | null;
  noticeDays: number | null;
  noticeAnchor: DeadlineNoticeAnchor;
  /** Gesellschaft des Postens — in der Konzern-Gesamtsicht gesetzt, sonst null */
  companyName?: string | null;
  customerId?: string;
  /** Kurzer Hinweis, was erkannt wurde bzw. unklar blieb. */
  note: string | null;
}

/** Ein Eintrag im Änderungsprotokoll. `action`/`source` siehe backend customerDeadlineChangeDiff.ts */
export interface DeadlineChangeEntry {
  id: string;
  deadlineId: string;
  deadlineName: string;
  action: string;
  source: string;
  actor: string | null;
  changes: { field: string; from: string | number | boolean | null; to: string | number | boolean | null }[];
  note: string | null;
  createdAt: string;
}

/** Bildadresse eines Belegs — die Anmeldung läuft über das Cookie, deshalb reicht ein <img src>. */
export function deadlineProofUrl(deadlineId: string, proofId: string): string {
  return `${API_BASE}/customer-deadlines/item/${encodeURIComponent(deadlineId)}/proofs/${encodeURIComponent(proofId)}`;
}

export function createCustomerDeadlinesApi(request: RequestFn) {
  return {
    async getCustomerDeadlines(ident: { customerId?: string; weclappCustomerId?: string; cwCompanyId?: number }) {
      const params = new URLSearchParams();
      if (ident.customerId) params.set('customerId', ident.customerId);
      if (ident.weclappCustomerId) params.set('weclappCustomerId', ident.weclappCustomerId);
      if (ident.cwCompanyId != null) params.set('cwCompanyId', String(ident.cwCompanyId));
      return request<{
        customer: {
          id: string;
          name: string;
          deadlineNotifyEmails: string[];
          mailContacts: { id: string; name: string; email: string | null; optIn: boolean }[];
        };
        deadlines: TechnicianDeadline[];
      }>(
        `/customer-deadlines?${params.toString()}`
      );
    },

    async getUpcomingDeadlines(days = 90) {
      return request<{ days: number; deadlines: UpcomingDeadline[] }>(
        `/customer-deadlines/upcoming?days=${encodeURIComponent(String(days))}`
      );
    },

    async createCustomerDeadline(customerId: string, data: CustomerDeadlineInput & { name: string }) {
      return request<{ deadline: TechnicianDeadline }>(`/customer-deadlines/${encodeURIComponent(customerId)}`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async updateCustomerDeadline(id: string, data: CustomerDeadlineInput) {
      return request<{ deadline: TechnicianDeadline }>(`/customer-deadlines/item/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    async updateDeadlineNotifyEmails(
      customerId: string,
      body: { emails?: string[]; contactOptIns?: Record<string, boolean> }
    ) {
      return request<{ ok: boolean; emails: string[] | null }>(`/customer-deadlines/${encodeURIComponent(customerId)}/notify-emails`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },

    async resolveCustomerDeadlineRequest(id: string) {
      return request<{ deadline: TechnicianDeadline }>(`/customer-deadlines/item/${encodeURIComponent(id)}/resolve-request`, {
        method: 'POST',
      });
    },

    /** Änderungsprotokoll eines Kunden, optional nur für einen Posten — neueste zuerst. */
    async getCustomerDeadlineChanges(customerId: string, deadlineId?: string) {
      const qs = deadlineId ? `?deadlineId=${encodeURIComponent(deadlineId)}` : '';
      return request<{ changes: DeadlineChangeEntry[] }>(
        `/customer-deadlines/${encodeURIComponent(customerId)}/changes${qs}`
      );
    },

    async uploadDeadlineProof(
      deadlineId: string,
      body: { base64: string; filename?: string; visibleToCustomer?: boolean; note?: string }
    ) {
      return request<{ proof: DeadlineProof }>(`/customer-deadlines/item/${encodeURIComponent(deadlineId)}/proofs`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async updateDeadlineProof(deadlineId: string, proofId: string, body: { visibleToCustomer?: boolean; note?: string }) {
      return request<{ proof: DeadlineProof }>(
        `/customer-deadlines/item/${encodeURIComponent(deadlineId)}/proofs/${encodeURIComponent(proofId)}`,
        { method: 'PATCH', body: JSON.stringify(body) }
      );
    },

    async deleteDeadlineProof(deadlineId: string, proofId: string) {
      return request<{ success: true }>(
        `/customer-deadlines/item/${encodeURIComponent(deadlineId)}/proofs/${encodeURIComponent(proofId)}`,
        { method: 'DELETE' }
      );
    },

    async deleteCustomerDeadline(id: string) {
      return request<{ success: true }>(`/customer-deadlines/item/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
    },

    /**
     * Screenshot oder PDF einer Lizenz-/Vertragsunterlage hochladen; KIAN liest
     * die Felder aus. Speichert nichts — die Werte landen nur als Vorschlag im
     * Formular und werden vom Techniker geprüft.
     */
    async prefillDeadlineFromDocument(file: File) {
      const form = new FormData();
      form.append('file', file);
      return request<{ prefill: DeadlinePrefill }>('/customer-deadlines/prefill-from-document', {
        method: 'POST',
        body: form,
      });
    },
  };
}
